import { useState } from 'react';

import { lockIndirectTaxFilingV2 } from '@commenda/api/indirect-tax/filings';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Icons,
} from '@commenda/kakashi';

interface IndirectTaxFilingApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  corporationId: number;
  onApproved?: () => void;
}

const IndirectTaxFilingApproveModal = ({
  isOpen,
  onClose,
  filingId,
  corporationId,
  onApproved,
}: IndirectTaxFilingApproveModalProps) => {
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setIsApproving(true);
    setError(null);
    const result = await lockIndirectTaxFilingV2({ filingId, corporationId });
    setIsApproving(false);
    if (result.success) {
      onApproved?.();
      onClose();
    } else {
      setError(result.error ?? 'Failed to approve filing');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="p-none flex flex-col gap-xl min-w-[420px]">
        <div className="p-3xl pb-none">
          <div className="size-12 rounded-md bg-utility-blue-50 flex justify-center items-center">
            <Icons.CheckCircle className="size-6 text-utility-blue-600" />
          </div>
        </div>
        <div className="px-3xl">
          <h3 className="txt-lg-bold text-text-primary mb-sm">
            Approve this filing?
          </h3>
          <p className="txt-small-regular text-text-secondary">
            Once approved, the Commenda team will submit this filing on your
            behalf. You will not be able to edit the transactions for this
            period after approval.
          </p>
          {error && (
            <p className="txt-small-regular text-text-error mt-sm">{error}</p>
          )}
        </div>
        <DialogFooter className='border-none'>
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleApprove}
            disabled={isApproving}
          >
            {isApproving ? 'Approving...' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxFilingApproveModal };
